import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, PlayCircle, Video } from 'lucide-react';

const CARD = '#23262C';
const ORANGE = '#FF8C08';

function formatCount(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

/**
 * ProfileAtomItem — a masonry card showing a static cover image for a
 * listing's "Atom" (short video). Priority order for the cover:
 *   1. thumbnailUrl (generated at upload time from the compressed video)
 *   2. imageUrls[0] (first photo if the seller also added photos)
 *   3. Placeholder icon
 *
 * No <video> element is created here — this keeps the profile grid fast,
 * it's just a lightweight <img>. Tapping navigates to full-screen playback.
 *
 * Props:
 * - listing: object (Firestore listing data, including `id`)
 * - index: number — position within `allListings`, so full-screen playback
 *   can start from the right item
 * - allListings: array — the full ordered list of atom listings, so the
 *   full-screen view can support vertical paging from the tapped item onward
 */
export default function ProfileAtomItem({ listing, index, allListings }) {
  const navigate = useNavigate();
  const [imgState, setImgState] = useState('loading'); // 'loading' | 'loaded' | 'error'

  const thumb = listing.thumbnailUrl?.toString() || '';
  const photos = Array.isArray(listing.imageUrls) ? listing.imageUrls : null;
  const coverUrl = thumb || (photos && photos.length > 0 ? photos[0]?.toString() || '' : '');

  const viewCountRaw = listing.viewCount;
  const viewCount = typeof viewCountRaw === 'number' ? Math.trunc(viewCountRaw) : 0;

  const openAtomView = () => {
    navigate(`/atom/${listing.id}`, {
      state: { listings: allListings, initialIndex: index },
    });
  };

  return (
    <div
      onClick={openAtomView}
      style={{
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: CARD,
        border: '1px solid rgba(255,255,255,0.06)',
        cursor: 'pointer',
      }}
    >
      {/* Cover image */}
      {coverUrl && imgState !== 'error' ? (
        <>
          <img
            src={coverUrl}
            alt=""
            onLoad={() => setImgState('loaded')}
            onError={() => setImgState('error')}
            style={{
              width: '100%',
              height: 180,
              objectFit: 'cover',
              display: imgState === 'loaded' ? 'block' : 'none',
            }}
          />
          {imgState === 'loading' && <ShimmerBox />}
        </>
      ) : (
        <Placeholder />
      )}

      {/* Dark gradient at bottom */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 56,
          background: 'linear-gradient(to top, rgba(0,0,0,0.87), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Play icon overlay */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Play color="#fff" size={16} fill="#fff" />
      </div>

      {/* "Atom" label badge */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '3px 6px',
          borderRadius: 6,
          backgroundColor: `${ORANGE}E6`,
        }}
      >
        <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: 0.3 }}>Atom</span>
      </div>

      {/* View count */}
      {viewCount > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 6,
            left: 8,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <PlayCircle color="rgba(255,255,255,0.80)" size={11} />
          <div style={{ width: 3 }} />
          <span style={{ color: 'rgba(255,255,255,0.80)', fontSize: 11, fontWeight: 600 }}>
            {formatCount(viewCount)}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Shimmer placeholder (animated, lightweight) ──────────────────────────
function ShimmerBox() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        height: 180,
        backgroundColor: CARD,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          animation: 'atomitem-shimmer 900ms ease-in-out infinite alternate',
        }}
      />
      <style>{`
        @keyframes atomitem-shimmer {
          from { opacity: 0.04; }
          to { opacity: 0.12; }
        }
      `}</style>
    </div>
  );
}

// ── Static placeholder ───────────────────────────────────────────────────
function Placeholder() {
  return (
    <div
      style={{
        height: 180,
        backgroundColor: CARD,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Video color="rgba(255,255,255,0.18)" size={36} />
    </div>
  );
}

export { ProfileAtomItem };